import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Grid,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Alert,
    Divider,
    Badge
} from '@mui/material';
import {
    Search,
    TrendingUp,
    TrendingDown,
    Email,
    Business,
    Phone,
    CalendarToday,
    Refresh,
    Info,
    FilterList,
    Warning,
    CheckCircle,
    Error as ErrorIcon,
    PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../../../../../API';
import LeadConversionDialog from './leadconversiondialog';

function LeadCard({ lead, onViewDetails, onConvertToClient }) {
    const { contact, score } = lead;
    
    const getTierColor = (tier) => {
        switch (tier) {
            case 'hot': return 'error';
            case 'warm': return 'warning';
            case 'cold': return 'info';
            default: return 'default';
        }
    };
    
    const getTierIcon = (tier) => {
        switch (tier) {
            case 'hot': return <TrendingUp />;
            case 'warm': return <TrendingDown />;
            default: return <TrendingDown />;
        }
    };

    return (
        <Card sx={{ height: '100%', position: 'relative' }}>
            <CardContent>
                {/* Tier Badge */}
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Chip
                        label={score.tier.toUpperCase()}
                        color={getTierColor(score.tier)}
                        size="small"
                        icon={getTierIcon(score.tier)}
                    />
                </Box>

                {/* Contact Info */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {contact.first_name} {contact.last_name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            {contact.email}
                        </Typography>
                    </Box>
                    
                    {contact.company_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Business fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {contact.company_name}
                            </Typography>
                        </Box>
                    )}
                    
                    {contact.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {contact.phone}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Score Breakdown */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Score: {score.total_score}/100
                    </Typography>
                    <LinearProgress 
                        variant="determinate" 
                        value={score.total_score} 
                        color={getTierColor(score.tier)}
                        sx={{ height: 8, borderRadius: 1, mb: 1 }}
                    />
                    
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Email: {score.email_quality}/{score.email_quality <= 35 ? '35' : '30'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Recency: {score.recency}/{score.recency <= 25 ? '25' : '20'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Engagement: {score.engagement}/{score.engagement <= 25 ? '25' : '20'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Profile: {score.profile_completeness || score.category_quality || 0}/{score.profile_completeness !== undefined ? '20' : '25'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Recommendation */}
                {score.recommendation && (
                    <Alert 
                        severity="info" 
                        sx={{ mb: 2, py: 0.5 }}
                    >
                        <Typography variant="caption">
                            {score.recommendation}
                        </Typography>
                    </Alert>
                )}

                {/* Reasons */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                        Strengths:
                    </Typography>
                    {score.reasons.slice(0, 2).map((reason, idx) => (
                        <Typography key={idx} variant="caption" color="success.main" sx={{ display: 'block' }}>
                            • {reason}
                        </Typography>
                    ))}
                </Box>

                {/* Red Flags */}
                {score.red_flags && score.red_flags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            ⚠️ Red Flags:
                        </Typography>
                        {score.red_flags.map((flag, idx) => (
                            <Typography key={idx} variant="caption" color="error" sx={{ display: 'block' }}>
                                • {flag}
                            </Typography>
                        ))}
                    </Box>
                )}

                {/* Metadata and Actions */}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                            {contact.last_contacted 
                                ? new Date(contact.last_contacted).toLocaleDateString()
                                : new Date(contact.created_at).toLocaleDateString()
                            }
                        </Typography>
                    </Box>
                    <Button 
                        size="small" 
                        onClick={() => onViewDetails(contact.id)}
                        startIcon={<Info />}
                    >
                        Details
                    </Button>
                </Box>

                {/* Convert to Client Button */}
                <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={(e) => {
                        e.stopPropagation();
                        onConvertToClient(lead); // Pass the full lead object
                    }}
                    sx={{
                        mt: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                        }
                    }}
                >
                    Convert to Client
                </Button>
            </CardContent>
        </Card>
    );
}

function LeadDetailsDialog({ open, onClose, contactId }) {
    const { data, isLoading } = useQuery({
        queryKey: ['leadScore', contactId],
        queryFn: () => API.crm.getLeadScore(contactId),
        enabled: open && !!contactId
    });

    if (!data) return null;

    const { contact, score, recommendations } = data;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Lead Details: {contact.first_name} {contact.last_name}
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <LinearProgress />
                    </Box>
                ) : (
                    <Box>
                        {/* Contact Information */}
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Email</Typography>
                                        <Typography variant="body1">{contact.email}</Typography>
                                    </Grid>
                                    {contact.company_name && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="text.secondary">Company</Typography>
                                            <Typography variant="body1">{contact.company_name}</Typography>
                                        </Grid>
                                    )}
                                    {contact.phone && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="text.secondary">Phone</Typography>
                                            <Typography variant="body1">{contact.phone}</Typography>
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Source</Typography>
                                        <Typography variant="body1">{contact.contact_source || 'Unknown'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Status</Typography>
                                        <Chip label={contact.status || 'new'} size="small" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Total Proposals</Typography>
                                        <Typography variant="body1">{contact.total_proposals || 0}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Score Breakdown */}
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Score Breakdown</Typography>
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Total Score</Typography>
                                        <Typography variant="body2" fontWeight="bold">{score.total_score}/100</Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={score.total_score} 
                                        sx={{ height: 10, borderRadius: 1 }}
                                    />
                                </Box>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Email Quality</Typography>
                                        <Typography variant="h6">{score.email_quality}/{score.email_quality <= 35 ? '35' : '30'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Recency</Typography>
                                        <Typography variant="h6">{score.recency}/{score.recency <= 25 ? '25' : '20'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Engagement</Typography>
                                        <Typography variant="h6">{score.engagement}/{score.engagement <= 25 ? '25' : '20'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Profile</Typography>
                                        <Typography variant="h6">{score.profile_completeness || score.category_quality || 0}/{score.profile_completeness !== undefined ? '20' : '25'}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Scoring Reasons */}
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Why This Score?</Typography>
                                {score.reasons.map((reason, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                        <CheckCircle color="success" fontSize="small" sx={{ mr: 1, mt: 0.5 }} />
                                        <Typography variant="body2">{reason}</Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Red Flags */}
                        {score.red_flags && score.red_flags.length > 0 && (
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom color="error">Red Flags</Typography>
                                    {score.red_flags.map((flag, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                            <Warning color="error" fontSize="small" sx={{ mr: 1, mt: 0.5 }} />
                                            <Typography variant="body2">{flag}</Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        {/* Recommendations */}
                        {recommendations && recommendations.length > 0 && (
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Recommendations</Typography>
                                    {recommendations.map((rec, idx) => (
                                        <Alert key={idx} severity="info" sx={{ mb: 1 }}>
                                            {rec}
                                        </Alert>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        {/* Notes */}
                        {contact.notes && (
                            <Card sx={{ mt: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Notes</Typography>
                                    <Typography variant="body2">{contact.notes}</Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function InterestingLeadsTab({ showToast }) {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [tierFilter, setTierFilter] = useState('all');
    const [minScore, setMinScore] = useState(50);
    const [excludeFreeEmails, setExcludeFreeEmails] = useState(false);
    const [corporateOnly, setCorporateOnly] = useState(false);
    const [sourceFilter, setSourceFilter] = useState('all');
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState(null);
    
    // Conversion dialog state - now stores full lead data
    const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
    const [convertingLeadData, setConvertingLeadData] = useState(null);

    // Fetch lead statistics
    const { data: stats, isLoading: statsLoading } = useQuery({
        queryKey: ['leadStats'],
        queryFn: API.crm.getLeadStats,
        refetchOnWindowFocus: false
    });

    // Fetch qualified leads
    const { data: leadsData, isLoading: leadsLoading, refetch: refetchLeads } = useQuery({
        queryKey: ['qualifiedLeads', { tierFilter, minScore, excludeFreeEmails, corporateOnly, sourceFilter }],
        queryFn: () => API.crm.getQualifiedLeads({
            tier: tierFilter !== 'all' ? tierFilter : undefined,
            min_score: minScore,
            exclude_free_emails: excludeFreeEmails,
            corporate_only: corporateOnly,
            source: sourceFilter !== 'all' ? sourceFilter : undefined,
            limit: 50
        }),
        refetchOnWindowFocus: false,
        onError: (error) => {
            showToast(error.response?.data?.error || 'Failed to load leads', 'error');
        }
    });

    // Bulk update scores mutation
    const bulkUpdateMutation = useMutation(
        (params) => API.crm.bulkUpdateScores(params),
        {
            onSuccess: (data) => {
                showToast(`Updated ${data.updates.hot + data.updates.warm + data.updates.cold} contact statuses`, 'success');
                queryClient.invalidateQueries(['qualifiedLeads']);
                queryClient.invalidateQueries(['leadStats']);
            },
            onError: (error) => {
                showToast(error.response?.data?.error || 'Failed to update scores', 'error');
            }
        }
    );

    const handleBulkUpdate = (dryRun = true) => {
        bulkUpdateMutation.mutate({
            apply_updates: !dryRun,
            min_score: minScore,
            update_status: true
        });
    };

    const handleViewDetails = (contactId) => {
        setSelectedContactId(contactId);
        setDetailsDialogOpen(true);
    };

    // Handle conversion to client - now accepts full lead object
    const handleConvertToClient = (leadData) => {
        setConvertingLeadData(leadData);
        setConversionDialogOpen(true);
    };

    // Handle successful conversion
    const handleConversionSuccess = (result) => {
        showToast(`Successfully converted ${result.client.full_name} to client!`, 'success');
        refetchLeads(); // Refresh the leads list
    };

    const filteredLeads = leadsData?.leads?.filter(lead => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            lead.contact.email?.toLowerCase().includes(query) ||
            lead.contact.first_name?.toLowerCase().includes(query) ||
            lead.contact.last_name?.toLowerCase().includes(query) ||
            lead.contact.company_name?.toLowerCase().includes(query)
        );
    }) || [];

    return (
        <Box>
            {/* Stats Overview */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>Total Contacts</Typography>
                            <Typography variant="h4">{stats?.total_contacts || 0}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {stats?.excluded_contacts || 0} excluded (converted)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>Hot Leads</Typography>
                            <Typography variant="h4" color="error.main">
                                {stats?.qualified_leads?.hot || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Score ≥ 60
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>Warm Leads</Typography>
                            <Typography variant="h4" color="warning.main">
                                {stats?.qualified_leads?.warm || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Score 45-59
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>Tier 1 Corporate</Typography>
                            <Typography variant="h4" color="success.main">
                                {stats?.email_distribution?.tier_1_corporate || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Target clients
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Filters</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleBulkUpdate(true)}
                                disabled={bulkUpdateMutation.isLoading}
                            >
                                Test Bulk Update
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleBulkUpdate(false)}
                                disabled={bulkUpdateMutation.isLoading}
                            >
                                Apply Bulk Update
                            </Button>
                            <IconButton onClick={() => refetchLeads()} disabled={leadsLoading}>
                                <Refresh />
                            </IconButton>
                        </Box>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Search Leads"
                                placeholder="Search by name, email, company..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Tier</InputLabel>
                                <Select
                                    value={tierFilter}
                                    onChange={(e) => setTierFilter(e.target.value)}
                                    label="Tier"
                                >
                                    <MenuItem value="all">All Tiers</MenuItem>
                                    <MenuItem value="hot">Hot (≥60)</MenuItem>
                                    <MenuItem value="warm">Warm (45-59)</MenuItem>
                                    <MenuItem value="cold">Cold (&lt;45)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Min Score"
                                value={minScore}
                                onChange={(e) => setMinScore(parseInt(e.target.value))}
                                inputProps={{ min: 0, max: 100 }}
                            />
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Source</InputLabel>
                                <Select
                                    value={sourceFilter}
                                    onChange={(e) => setSourceFilter(e.target.value)}
                                    label="Source"
                                >
                                    <MenuItem value="all">All Sources</MenuItem>
                                    <MenuItem value="brand_deals">Brand Deals</MenuItem>
                                    <MenuItem value="partnerships">Partnerships</MenuItem>
                                    <MenuItem value="manual">Manual</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Email Type</InputLabel>
                                <Select
                                    value={corporateOnly ? 'corporate' : excludeFreeEmails ? 'no_free' : 'all'}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCorporateOnly(val === 'corporate');
                                        setExcludeFreeEmails(val === 'no_free' || val === 'corporate');
                                    }}
                                    label="Email Type"
                                >
                                    <MenuItem value="all">All Emails</MenuItem>
                                    <MenuItem value="no_free">No Free Emails</MenuItem>
                                    <MenuItem value="corporate">Corporate Only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {filteredLeads.length} of {leadsData?.total || 0} qualified leads
                        </Typography>
                        {leadsData?.stats && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip 
                                    label={`Hot: ${leadsData.stats.tier_distribution.hot}`}
                                    color="error"
                                    size="small"
                                />
                                <Chip 
                                    label={`Warm: ${leadsData.stats.tier_distribution.warm}`}
                                    color="warning"
                                    size="small"
                                />
                                <Chip 
                                    label={`Avg: ${leadsData.stats.average_score}`}
                                    size="small"
                                />
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Leads Grid */}
            {leadsLoading || statsLoading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <LinearProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading qualified leads...
                    </Typography>
                </Box>
            ) : filteredLeads.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <ErrorIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No leads match your filters
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Try adjusting your filters or lowering the minimum score
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={2}>
                    {filteredLeads.map((lead) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={lead.contact.id}>
                            <LeadCard 
                                lead={lead} 
                                onViewDetails={handleViewDetails}
                                onConvertToClient={handleConvertToClient}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Lead Details Dialog */}
            <LeadDetailsDialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                contactId={selectedContactId}
            />

            {/* Lead Conversion Dialog - Now accepts leadData prop */}
            <LeadConversionDialog
                open={conversionDialogOpen}
                onClose={() => {
                    setConversionDialogOpen(false);
                    setConvertingLeadData(null);
                }}
                leadData={convertingLeadData}
                onSuccess={handleConversionSuccess}
            />
        </Box>
    );
}